import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'
import { AuthShell } from './SignInPage'

const Schema = z.object({ email: z.string().email() })
type FormValues = z.infer<typeof Schema>

export function ForgotPasswordPage() {
  const t = useT()
  const [sent, setSent] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(Schema),
  })

  const forgot = useMutation({
    mutationFn: (values: FormValues) => apiClient.post('/auth/forgot-password', values),
    onSuccess: () => setSent(true),
  })

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold mb-1">{t('auth.forgot.title')}</h1>
      <p className="text-sm opacity-70 mb-6">{t('auth.forgot.subtitle')}</p>

      {sent ? (
        <div className="text-sm opacity-80">
          <p>{t('auth.forgot.sent')}</p>
          <Link to="/auth/signin" className="underline mt-4 inline-block">{t('auth.signin.link')}</Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit((v) => forgot.mutate(v))} className="flex flex-col gap-3">
          <label className="block">
            <span className="text-sm opacity-80">{t('auth.email')}</span>
            <input {...register('email')} type="email" autoFocus className="auth-input mt-1" />
            {errors.email && <p className="text-xs mt-1" style={{ color: 'var(--color-critical)' }}>{errors.email.message}</p>}
          </label>
          <button
            type="submit"
            disabled={forgot.isPending}
            className="mt-1 px-3 py-2 rounded font-medium text-sm"
            style={{ background: 'var(--color-accent)', color: 'white' }}
          >
            {forgot.isPending ? t('common.loading') : t('auth.forgot.cta')}
          </button>
          <Link to="/auth/signin" className="text-sm hover:underline mt-2">{t('auth.signin.link')}</Link>
        </form>
      )}
    </AuthShell>
  )
}
